// Dependencies
import React, { Children, cloneElement } from 'react';
import { Modal, Grid, Icon, Skeleton } from '@mui/material';

// Components
import { Button, Loading } from 'Components';
import { Tooltip } from 'Components';

// Styles
import './modal.scss';

export const ModalBody = (props) => {
  const { mainProps = {}, hideDefaultStyles, className, style, children, ...rest } = props;
  const defaultStyles = 'mt-50 mb-100';
  const position = mainProps?.position;

  return (
    <Grid
      {...rest}
      style={style}
      className={`modal-body ${(position && !hideDefaultStyles) && defaultStyles} ${className || ''}`}
    >
      {children}
    </Grid>
  );
};

export const ModalFooter = (props) => {
  const { mainProps = {}, className, style, styleDiv, children, ...rest } = props;
  const { position, size = 'medium' } = mainProps;

  return (
    <Grid
      {...rest}
      className={`modal-footer ${className || ''} ${position ? `modal-footer-fixed modal-footer-${position}` : ''} modal-footer-${size}`}
      justifyContent="flex-start"
      container
      direction="row"
      style={style}
    >
      <div style={{ ...(styleDiv || {}), zIndex: 2, width: "100%" }}>{children}</div>
    </Grid>
  );
};

const splitTitle = [
  {
    size: 'small',
    maxLength: 48
  },
  {
    size: 'medium',
    maxLength: 80
  },
  {
    size: 'large',
    maxLength: 100
  }
];

/**
 * Renders a side menu component.
 * @property {string} size -  extra-small | small | medium | large.
 * @returns {React.Node} The Modal component.
 */
const ModalComponent = (props) => {
  const { header = true } = props;

  const rootRef = React.useRef(null);

  let childs = [];
  if (Array.isArray(props.children)) {
    childs = props.children.map((child) => child);
  } else {
    childs.push(props.children);
  }

  const childrenWithProps = Children.map(childs, (child) => {
    // Only pass mainProps to ModalBody and ModalFooter components
    if (child?.type === ModalBody || child?.type === ModalFooter) {
      return cloneElement(child, { mainProps: props });
    }
    return child;
  });

  const getTitleSplited = (title, maxLength) => {
    return `${title.slice(0, maxLength)} ...`;
  }

  const renderTitle = (title, size) => {
    if (title) {
      const sizesToSplit = splitTitle.find(sizeToSplit => sizeToSplit.size === size);
      if (sizesToSplit && title.length >= sizesToSplit.maxLength) {
        return (
          <Tooltip title={title} placement={'bottom'}>
            <span>{getTitleSplited(title, sizesToSplit.maxLength)}</span>
          </Tooltip>
        )
      } else {
        return title;
      }
    }
    return '';
  }

  return (
    <div className="modal-root" ref={rootRef}>

      <Modal
        slotProps={props?.disableBackdropClick ? { backdrop: { sx: { display: 'none' } } } : {}} // Oculta el backdrop completamente
        disablePortal
        disableEnforceFocus
        disableAutoFocus
        open={props.open}
        aria-labelledby="modal-title"
        aria-describedby="modal-description"
        className={`modal-modal ${props.className ? props.className : ''} ${props.position && 'modal-' + props.position}`}
        container={() => rootRef.current}
        style={{ ...props.style }}
        onClose={props.onClose}
      >
        <div className={`modal-content modal-${props.size ? props.size : 'medium'} ${props.position && 'all-height'} ${props.className || ''}`} >
          {
            props.loading &&
            <Loading overlay show />
          }

          {props.title
            && header &&
            <Grid
              container
              className={!props.hiddenStyleTitle && `modal-header ${props.position && 'modal-header-fixed modal-header-' + props.position} modal-header-${props.size ? props.size : 'medium'}` || ''}
              style={{ ...props.headerStyle }}
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              {props.iconBackClose && (
                <Grid item className="text-left">

                  <Button
                    type="icon"
                    icon="arrow_back"
                    className="btn-close"
                    onClick={() => {
                      if (props.handleBack) {
                        props.handleBack();
                        return;
                      }

                      if (props.handleClose) {
                        props.handleClose();
                      }
                    }}
                  />
                </Grid>
              )}
              <Grid item className="text-right">
                {props.useCloseButtonTop && (
                  <Button
                    type="icon"
                    icon="close"
                    className="btn-close"
                    onClick={() => {
                      if (props.handleClose) {
                        props.handleClose();
                      }
                    }}
                  />
                )}
              </Grid>
              <Grid item xs style={{ display: 'flex' }}>
                {props.headerIcon && <Icon>{props.headerIcon}</Icon>}
                {
                  !props.title && props.isSkeletonLoading ? <Skeleton variant="text" width={100} height={30} /> :
                    <span style={{ ...props.modalTitleStyles }} className="modal-title ">{renderTitle(props.title, props.size)}</span>
                }
              </Grid>

              <Grid item className="text-right">
                {props.useExpandMore && (
                  <Button
                    type="icon"
                    icon="expand_more"
                    className="btn-close"
                    onClick={() => {
                      if (props.handleExpand) {
                        props.handleExpand();
                      }
                    }}
                  />
                )}
              </Grid>

              <Grid item className="text-right">
                {!props.hiddenCloseButton && (
                  <Button
                    type="icon"
                    icon="close"
                    className="btn-close"
                    onClick={() => {
                      if (props.handleClose) {
                        props.handleClose();
                      }
                    }}
                  />
                )}
              </Grid>
            </Grid>
          }

          {childrenWithProps}
        </div>
      </Modal>
    </div>
  );
};

export default ModalComponent;